import React from "react";
import { MoreVertical } from "lucide-react";

export type GeneralDashboardCardTitleVariant = "default" | "large" | "hidden";

interface GeneralDashboardCardProps {
  title: string;
  onMenuClick?: () => void;
  children: React.ReactNode;
  className?: string;
  minHeight?: string;
  titleVariant?: GeneralDashboardCardTitleVariant;
  headerSlot?: React.ReactNode;
  padding?: "default" | "compact" | "tight";
  menuTestId?: string;
}

export const GeneralDashboardCard: React.FC<GeneralDashboardCardProps> = ({
  title,
  onMenuClick,
  children,
  className = "",
  minHeight,
  titleVariant = "default",
  headerSlot,
  padding = "default",
  menuTestId,
}) => {
  const paddingClass = {
    default: "px-4 py-2",
    compact: "px-4 py-1.5",
    tight: "p-2",
  }[padding];

  const titleClass =
    titleVariant === "large"
      ? "text-xl font-bold leading-normal text-foreground"
      : "text-lg font-bold leading-normal text-foreground";

  const menuButton = onMenuClick ? (
    <button
      onClick={onMenuClick}
      className="flex h-8 max-w-9 shrink-0 items-center justify-center rounded-xl px-3 py-1 text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
      aria-label={`More options ${title}`}
      data-testid={
        menuTestId ??
        `more-options-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`
      }
      type="button"
    >
      <MoreVertical size={24} />
    </button>
  ) : null;

  const showTitle = titleVariant !== "hidden";

  return (
    <div
      className={[
        "general-dashboard-card flex flex-col rounded-2xl border border-border/80 bg-background-1 shadow-sm",
        paddingClass,
        className,
      ].join(" ")}
      style={minHeight ? { minHeight } : undefined}
    >
      {(showTitle || headerSlot || menuButton) && (
        <div
          className={[
            "flex items-center gap-2",
            showTitle || headerSlot ? "mb-1 justify-between" : "mb-1 justify-end",
          ].join(" ")}
        >
          {(headerSlot || showTitle) && (
            <div className="flex min-w-0 flex-1 items-center gap-2">
              {headerSlot}
              {showTitle ? <h3 className={titleClass}>{title}</h3> : null}
            </div>
          )}
          {!showTitle ? <span className="sr-only">{title}</span> : null}
          {menuButton}
        </div>
      )}
      <div className="flex flex-1 flex-col">{children}</div>
    </div>
  );
};
